import type { Company, Lead } from '../tracker/types';
import { companyLeadsOf } from '../tracker/types';

/**
 * How the profiles point at each other.
 *
 * Kyu, 9 September, holding HubSpot next to the Hub: "they are all
 * interlinked and you can click from any of the pages." These are the
 * addresses each profile answers at, and the one derivation the deal drawer
 * needs to list its contacts. docs/design-note-profiles-interlinked.md has
 * the argument.
 */

export function leadHref(leadId: string): string {
  return `/tracker?lead=${leadId}`;
}

export function companyHref(companyId: string): string {
  return `/tracker?tab=companies&company=${companyId}`;
}

export function orderHref(orderId: string): string {
  return `/cg-orders/${orderId}`;
}

export interface DealContact {
  lead: Lead;
  /** The deal's own Contact field. Listed first. */
  primary: boolean;
}

/**
 * Everyone the deal is talking to: its own contact, then everyone else at
 * the same company.
 *
 * "Same company" is the lead's Company field, never the email domain. Kyu:
 * "Wave Browser, that's really the name of their company, but their domain
 * is 8-point-something. I don't want it to be too rigid. We can't not add
 * actual people just because they have different domains or because they're
 * using gmail." So a @gmail.com lead whose Company is Wave Browser is on
 * Wave Browser's deals, and a lead at some other company who happens to
 * share a domain is not. The rule is companyLeadsOf, the same one the
 * company panel already uses, so the two screens cannot disagree.
 *
 * Derived on every read from rows that already exist. Nothing is written.
 * If a deal ever needs a contact from a *different* company, that is the day
 * for a join table, not before.
 *
 * Order: primary first, then most recently touched, then by name — the one
 * rule for every list on every drawer.
 */
export function dealContacts(
  deal: { lead_id: string | null; company_id: string | null },
  companies: readonly Company[],
  leads: readonly Lead[],
): DealContact[] {
  const primary = deal.lead_id === null ? undefined : leads.find((l) => l.id === deal.lead_id);
  const company =
    deal.company_id === null ? undefined : companies.find((c) => c.id === deal.company_id);
  const atCompany = company === undefined ? [] : companyLeadsOf(company, [...leads]);
  const byTouch = newestFirst<Lead>((lead) => lead.last_touch_at);
  const others = atCompany
    .filter((lead) => lead.id !== primary?.id)
    .sort((a, b) => byTouch(a, b) || leadName(a).localeCompare(leadName(b)));
  const rows: DealContact[] = [];
  if (primary !== undefined) rows.push({ lead: primary, primary: true });
  for (const lead of others) rows.push({ lead, primary: false });
  return rows;
}

export function leadName(lead: Pick<Lead, 'first_name' | 'last_name' | 'email'>): string {
  const name = `${lead.first_name} ${lead.last_name}`.trim();
  return name === '' ? lead.email : name;
}

/**
 * The one ordering rule: most recent at the top, oldest at the bottom.
 * Rows with no date go last, so a missing date can never hide a real one.
 * ISO strings compare correctly as text, which is why no Date is built.
 */
export function newestFirst<T>(at: (row: T) => string | null | undefined): (a: T, b: T) => number {
  return (a, b) => {
    const ta = at(a) ?? '';
    const tb = at(b) ?? '';
    if (ta === tb) return 0;
    if (ta === '') return 1;
    if (tb === '') return -1;
    return ta < tb ? 1 : -1;
  };
}
